import { useTheme } from '@/hooks/useTheme';

const steps = [
  {
    number: '01',
    title: 'Audit',
    description: 'We analyze your current workflows, communication gaps, and operational inefficiencies. Every gap becomes a roadmap point.',
    icon: 'ri-search-eye-line',
    accent: 'cyan',
    detail: 'Deep-dive analysis across systems, teams, and workflows.',
  },
  {
    number: '02',
    title: 'Architecture',
    description: 'We design a system tailored to your business structure and industry-specific demands. No generic templates.',
    icon: 'ri-layout-grid-line',
    accent: 'violet',
    detail: 'Custom system design mapped to your exact operational model.',
  },
  {
    number: '03',
    title: 'Deployment',
    description: 'We implement automation, AI agents, and system integrations with precision. Fast, structured, zero downtime.',
    icon: 'ri-rocket-2-line',
    accent: 'cyan',
    detail: 'Precision implementation with full testing and quality assurance.',
  },
  {
    number: '04',
    title: 'Optimization',
    description: 'We continuously refine performance to ensure long-term scalability and control. The system gets smarter over time.',
    icon: 'ri-line-chart-line',
    accent: 'violet',
    detail: 'Continuous performance tuning for maximum long-term impact.',
  },
];

export default function ProcessSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section id="process" className="py-28 transition-colors duration-500" style={{ background: isDark ? '#0E1116' : '#ffffff' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-px ${isDark ? 'bg-cyan-400/50' : 'bg-blue-500/50'}`}></div>
              <span className={`text-xs tracking-widest uppercase font-medium ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}>Process</span>
            </div>
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
              Structured Execution.
              <br />
              <span className="text-gradient-cyan">Predictable Results.</span>
            </h2>
          </div>
          <p className={`text-base max-w-sm leading-relaxed lg:text-right ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            Every engagement follows a deliberate methodology—no improvisation, no guesswork. Just structured execution from day one.
          </p>
        </div>

        {/* Full-width process visual */}
        <div className={`relative rounded-2xl overflow-hidden mb-10 border transition-colors duration-500 ${
          isDark ? 'border-white/6' : 'border-black/6'
        }`} style={{ height: '300px' }}>
          <img
            src="https://public.readdy.ai/ai/img_res/6b2f4142153797057a478e050532c33a.jpg"
            alt="Process Flow"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{
            background: isDark
              ? 'linear-gradient(to bottom, transparent, transparent, #0E1116)'
              : 'linear-gradient(to bottom, transparent, transparent, #ffffff)'
          }}></div>
          <div className="absolute inset-0" style={{
            background: isDark
              ? 'linear-gradient(to right, rgba(14,17,22,0.5), transparent, rgba(14,17,22,0.5))'
              : 'linear-gradient(to right, rgba(255,255,255,0.4), transparent, rgba(255,255,255,0.4))'
          }}></div>

          {/* Step labels over image */}
          <div className="absolute inset-0 flex items-center">
            <div className="w-full max-w-7xl mx-auto px-10 flex items-center justify-around">
              {steps.map((step, idx) => (
                <div key={idx} className="flex flex-col items-center gap-3 text-center">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-full border backdrop-blur-md ${
                    step.accent === 'cyan'
                      ? isDark ? 'bg-cyan-400/15 border-cyan-400/30' : 'bg-blue-500/15 border-blue-500/30'
                      : isDark ? 'bg-violet-400/15 border-violet-400/30' : 'bg-purple-500/15 border-purple-500/30'
                  }`}>
                    <i className={`${step.icon} text-base ${
                      step.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                    }`}></i>
                  </div>
                  <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{step.title}</p>
                  <p className={`text-xs hidden lg:block max-w-[120px] leading-snug ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{step.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Step cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={`relative flex flex-col gap-5 p-8 rounded-2xl border transition-all duration-300 ${
                isDark
                  ? 'border-white/6 hover:border-white/12 hover:bg-[#111827]'
                  : 'border-black/6 hover:border-black/12 hover:bg-gray-50 shadow-sm'
              }`}
              style={{ background: isDark ? '#111827' : '#ffffff' }}
            >
              {/* Number */}
              <span className="text-5xl font-bold leading-none tracking-tighter" style={{ color: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
                {step.number}
              </span>

              {/* Icon */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${
                step.accent === 'cyan'
                  ? isDark ? 'bg-cyan-400/10 border border-cyan-400/20' : 'bg-blue-500/10 border border-blue-500/20'
                  : isDark ? 'bg-violet-400/10 border border-violet-400/20' : 'bg-purple-500/10 border border-purple-500/20'
              }`}>
                <i className={`${step.icon} text-sm ${
                  step.accent === 'cyan' ? (isDark ? 'text-cyan-400' : 'text-blue-500') : (isDark ? 'text-violet-400' : 'text-purple-500')
                }`}></i>
              </div>

              <h3 className={`text-xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{step.title}</h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}